import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { confirmSignUp, resendSignUpCode } from "../amplify";
import PublicShell from "../layout/PublicShell";
import { motion } from "framer-motion";
import { cn } from "../lib/format";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 30;

function maskedEmail(email: string): string {
  const at = email.indexOf("@");
  if (at <= 0) return email;
  const head = email.slice(0, at);
  const visible = head.slice(0, Math.min(4, head.length));
  return `${visible}…`;
}

export default function Verify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initialEmail = params.get("email") ?? "";
  const [email, setEmail] = useState(initialEmail);
  const [editingEmail, setEditingEmail] = useState(false);
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(""));
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [sentAt] = useState(() =>
    new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
  );
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  // The page can be reached via three paths:
  //   1. Email signup form -> nav("/verify?email=foo@bar")
  //   2. Cognito OAuth callback -> /verify?code=AUTH&state=STATE (Amplify
  //      processes these on module load; once it does the user is signed
  //      in and we want them on the dashboard, not stuck on the OTP page
  //      with a now-stale code in the URL).
  //   3. Direct navigation / refresh / back-button — neither code nor
  //      email — there's literally nothing to confirm. Bounce to /signup
  //      so the user re-enters the flow with a real email.
  useEffect(() => {
    const hasCode = !!params.get("code");
    const hasEmail = !!params.get("email");
    if (hasCode && !hasEmail) {
      nav("/app/today", { replace: true });
    } else if (!hasCode && !hasEmail) {
      nav("/signup", { replace: true });
    }
  }, [params, nav]);

  // Focus the first cell on mount.
  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  // Resend cooldown timer.
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  // Auto-submit when all six cells are filled.
  useEffect(() => {
    const code = digits.join("");
    if (code.length === CODE_LENGTH && !busy && !ok) {
      void submit(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  async function submit(code: string) {
    setErr(null);
    setBusy(true);
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      setOk("Email confirmed. Redirecting…");
      setTimeout(() => nav("/login", { replace: true }), 700);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "That code didn't work — try again.");
      setDigits(Array(CODE_LENGTH).fill(""));
      refs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  }

  function onCellChange(i: number, raw: string) {
    // Strip non-digits; if multiple digits arrive (paste from a non-paste
    // source, or autofill), distribute across subsequent cells.
    const cleaned = raw.replace(/\D/g, "");
    if (!cleaned) {
      setDigits((prev) => prev.map((d, idx) => (idx === i ? "" : d)));
      return;
    }
    setDigits((prev) => {
      const next = [...prev];
      for (let k = 0; k < cleaned.length && i + k < CODE_LENGTH; k++) {
        next[i + k] = cleaned[k];
      }
      return next;
    });
    const lastIdx = Math.min(i + cleaned.length, CODE_LENGTH - 1);
    refs.current[lastIdx]?.focus();
  }

  function onCellKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      e.preventDefault();
      setDigits((prev) => prev.map((d, idx) => (idx === i - 1 ? "" : d)));
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < CODE_LENGTH - 1) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const txt = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!txt) return;
    e.preventDefault();
    setDigits(() => {
      const next = Array(CODE_LENGTH).fill("");
      for (let k = 0; k < txt.length; k++) next[k] = txt[k];
      return next;
    });
    const lastIdx = Math.min(txt.length, CODE_LENGTH) - 1;
    refs.current[lastIdx]?.focus();
  }

  async function onResend() {
    if (secondsLeft > 0) return;
    setErr(null);
    setOk(null);
    try {
      await resendSignUpCode({ username: email });
      setOk("A fresh code is on its way.");
      setSecondsLeft(RESEND_SECONDS);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Couldn't resend — check the address?");
    }
  }

  return (
    <PublicShell minimal>
      <div className="max-w-md mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="font-serif text-3xl md:text-4xl leading-tight">
            Confirm{" "}
            <span className="italic text-terracotta">{maskedEmail(email)}</span>
          </h1>
          <p className="mt-2 font-mono text-[12px] text-ink-faint">
            code sent · {sentAt}
          </p>

          <div className="mt-7 grid grid-cols-6 gap-2" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  refs.current[i] = el;
                }}
                value={d}
                onChange={(e) => onCellChange(i, e.target.value)}
                onKeyDown={(e) => onCellKeyDown(i, e)}
                onFocus={(e) => e.currentTarget.select()}
                inputMode="numeric"
                autoComplete={i === 0 ? "one-time-code" : "off"}
                maxLength={1}
                aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
                disabled={busy || !!ok}
                className={cn(
                  "h-14 w-full text-center font-mono text-2xl rounded-md border bg-paper",
                  "focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20",
                  d ? "border-ink" : "border-[rgba(42,36,26,0.18)]",
                  (busy || ok) && "opacity-60",
                )}
              />
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between text-[12px]">
            <span className="text-ink-faint">auto-confirms when full</span>
            <button
              type="button"
              onClick={onResend}
              disabled={secondsLeft > 0}
              className={cn(
                "font-mono text-[12px] underline decoration-ink-faint/40",
                secondsLeft > 0 ? "text-ink-faint cursor-default" : "text-ink-soft hover:decoration-terracotta hover:text-terracotta",
              )}
            >
              {secondsLeft > 0
                ? `resend in 0:${String(secondsLeft).padStart(2, "0")}`
                : "resend code"}
            </button>
          </div>

          {err && <div className="mt-4 text-sm text-terracotta font-medium">{err}</div>}
          {ok && <div className="mt-4 text-sm text-[#386641] font-medium">{ok}</div>}

          <div className="mt-8 pt-5 border-t border-[rgba(42,36,26,0.12)] text-[13px] text-ink-soft">
            Wrong email?{" "}
            {editingEmail ? (
              <span className="inline-flex items-center gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="font-mono text-[12px] px-2 py-1 rounded border border-[rgba(42,36,26,0.18)] bg-paper focus:outline-none focus:border-terracotta"
                />
                <button
                  type="button"
                  onClick={() => setEditingEmail(false)}
                  className="font-mono text-[11px] underline text-terracotta"
                >
                  done
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setEditingEmail(true)}
                className="underline decoration-ink-faint/40 hover:decoration-terracotta hover:text-terracotta"
              >
                Edit it
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </PublicShell>
  );
}
